import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import {
	trackCheckCtaClick,
	trackDemoRequestClick,
	trackPricingPlanClick,
} from "@/utils/analytics";
import { ROUTE_CONFIG } from "@/routes/config";

type Plan = {
	name: string;
	price: string;
	priceNote: string;
	description: string;
	features: string[];
	cta: string;
	highlighted?: boolean;
};

export default function Pricing() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ts = (key: string) => t(`marketing.pricing.${key}`);

	const plans = t("marketing.pricing.plans", { returnObjects: true }) as Plan[];

	// Plan order is fixed: 0 = free quick check, 1 = Professional, 2 = + consulting
	const handlePlanCta = (index: number) => {
		const planKeys = ["quick_check", "professional", "consulting"];

		trackPricingPlanClick(planKeys[index]);
		if (index === 0) {
			trackCheckCtaClick("pricing_page");
			navigate("/questionnaire/yes");
		} else {
			trackDemoRequestClick("pricing_page");
			navigate(ROUTE_CONFIG.CONTACT);
		}
	};

	return (
		<div className="w-full flex flex-col items-center font-urbanist">
			<section className="w-full px-4 md:px-16">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col gap-12">
					<div className="flex flex-col items-center text-center gap-6">
						<h1 className="text-[36px] md:text-[48px] font-bold leading-[120%] text-ink">
							{ts("title")}
						</h1>
						<p className="text-[18px] leading-[150%] text-subtle max-w-[760px]">{ts("subtitle")}</p>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
						{plans?.map((plan, index) => (
							<div
								key={plan.name}
								className={`flex flex-col gap-6 rounded-2xl px-8 py-10 border ${
									plan.highlighted
										? "border-accent border-2 shadow-raised-hover relative"
										: "border-primary-gray-lightest"
								}`}
							>
								{plan.highlighted && (
									<span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-ink text-white text-[12px] font-semibold rounded-pill px-4 py-1">
										{ts("highlightBadge")}
									</span>
								)}
								<div className="flex flex-col gap-2">
									<h2 className="text-[22px] font-bold text-ink">{plan.name}</h2>
									<div className="flex items-baseline gap-2">
										<span className="text-[36px] font-bold text-ink">{plan.price}</span>
										{plan.priceNote && (
											<span className="text-[14px] text-subtle">{plan.priceNote}</span>
										)}
									</div>
									<p className="text-[16px] leading-[150%] text-subtle">{plan.description}</p>
								</div>
								<ul className="flex flex-col gap-3 flex-1">
									{plan.features.map((feature) => (
										<li key={feature} className="flex items-start gap-3">
											<Icon className="w-4 h-4 mt-1 text-success shrink-0" icon={faCircleCheck} />
											<span className="text-[15px] leading-[150%] text-ink">{feature}</span>
										</li>
									))}
								</ul>
								<button
									className={`w-full py-3 rounded-button text-[16px] font-bold transition-colors ${
										plan.highlighted
											? "bg-accent hover:bg-accent-dark text-white"
											: "bg-white border border-primary-gray-lightest hover:bg-surface text-ink"
									}`}
									type="button"
									onClick={() => handlePlanCta(index)}
								>
									{plan.cta}
								</button>
							</div>
						))}
					</div>

					<p className="text-[14px] text-subtle text-center">{ts("footnote")}</p>
				</div>
			</section>
		</div>
	);
}
